import { http, isObject, toQuery, unwrap } from './http'
import { normalizeNotification, unexpectedNotificationFields } from '@/utils/notifications'
import {
  normalizeModel,
  normalizeModelHealth,
  normalizeProvider,
  selectUsableModels,
} from '@/utils/model-catalog'
import {
  normalizeAuditEntry,
  normalizeOpenwaNode,
  normalizeQueuesStatus,
  normalizeWebhookFailures,
} from '@/utils/operations'
import { normalizeOrganizationDetail, normalizeOrganizationRow } from '@/utils/organizations'
import { normalizePlan } from '@/utils/plans'
import { normalizePlatformUsageRow, toPeriod } from '@/utils/usage'

/** Rutas de `docs/API.md` § Consola de plataforma. Exigen token de rol de plataforma. */
export const platformApi = {
  /* ─── Clientes ──────────────────────────────────────────────────────────── */
  //
  // La respuesta son las trece columnas crudas del `SELECT` de
  // `listForPlatform`: `plan_name`, `messages_period`, `cost_usd_period`… La
  // tabla las leía en camelCase y por eso enseñaba el plan en blanco y el
  // consumo del mes a cero sobre clientes que sí consumen.
  async listOrganizations(params = {}, config = {}) {
    const data = await unwrap(
      http.get('/platform/organizations', { ...config, params: toQuery(params) }),
    )
    return asList(data).map(normalizeOrganizationRow)
  },

  createOrganization(payload) {
    return unwrap(http.post('/platform/organizations', payload), isObject)
  },

  /**
   * Ficha completa de un cliente.
   *
   * Devuelve un SOBRE (`{ organization, subscription, channels, bots, usage,
   * impersonations }`) con filas crudas dentro. Se traduce aquí, como el resto
   * de la consola: la vista leía `channel.phone`, `bot.enabled`, `dataSources` e
   * `incidents`, cuatro caminos que no existen en la respuesta.
   */
  async getOrganization(id, config = {}) {
    const data = await unwrap(
      http.get(`/platform/organizations/${encodeURIComponent(id)}`, config),
      isObject,
    )
    return normalizeOrganizationDetail(data)
  },

  updateOrganization(id, patch) {
    return unwrap(http.patch(`/platform/organizations/${encodeURIComponent(id)}`, patch), isObject)
  },

  /**
   * Suspensión de un cliente. El MOTIVO es obligatorio por contrato
   * (`suspendSchema`: `reason` de 5 a 500 caracteres) y sin él la API responde
   * 400 — que es lo que ocurría siempre, porque el panel llamaba sin cuerpo.
   *
   * Se exige aquí, en la frontera, en vez de confiar en que cada pantalla se
   * acuerde: el motivo es lo que queda escrito en `suspended_reason` y en la
   * auditoría, y es lo único que explica meses después por qué un cliente dejó
   * de recibir respuestas.
   *
   * @param {string} id
   * @param {string} reason
   */
  suspendOrganization(id, reason) {
    return unwrap(
      http.post(`/platform/organizations/${encodeURIComponent(id)}/suspend`, { reason }),
    )
  },

  reactivateOrganization(id) {
    return unwrap(http.post(`/platform/organizations/${encodeURIComponent(id)}/reactivate`))
  },

  /**
   * Suplantación auditada. El motivo es obligatorio por contrato y por
   * `impersonation_sessions`: sin él, el backend responde 400.
   * @param {string} id @param {string} reason
   */
  impersonate(id, reason) {
    return unwrap(
      http.post(`/platform/organizations/${encodeURIComponent(id)}/impersonate`, { reason }),
      isObject,
    )
  },

  /* ─── Usuarios de un cliente ────────────────────────────────────────────── */
  //
  // Administración de CUENTAS del cliente (quién entra y con qué rol). Los datos
  // del cliente —conversaciones, canales, configuración— siguen exigiendo
  // suplantación auditada: para eso está `impersonate`.

  listOrganizationUsers(organizationId, params = {}, config = {}) {
    return unwrap(
      http.get(`/platform/organizations/${encodeURIComponent(organizationId)}/users`, {
        ...config,
        params: toQuery(params),
      }),
    )
  },

  /** @param {{ email: string, name: string, role: string }} payload */
  inviteOrganizationUser(organizationId, payload) {
    return unwrap(
      http.post(`/platform/organizations/${encodeURIComponent(organizationId)}/users`, payload),
      isObject,
    )
  },

  /** @param {{ role?: string, status?: 'invited'|'active'|'disabled' }} patch */
  updateOrganizationUser(organizationId, membershipId, patch) {
    return unwrap(
      http.patch(
        `/platform/organizations/${encodeURIComponent(organizationId)}/users/${encodeURIComponent(membershipId)}`,
        patch,
      ),
      isObject,
    )
  },

  removeOrganizationUser(organizationId, membershipId) {
    return http.delete(
      `/platform/organizations/${encodeURIComponent(organizationId)}/users/${encodeURIComponent(membershipId)}`,
    )
  },

  /**
   * Enlace de un solo uso para que esa persona establezca su contraseña.
   *
   * Devuelve el enlace al operador —no se envía por correo— y consumirlo tumba
   * todas las sesiones anteriores del usuario. Es la vía para desatascar a un
   * cliente que nunca recibió su invitación y para recuperar una cuenta
   * secuestrada; el enlace no se guarda en ninguna parte, se usa y se olvida.
   */
  issueUserPasswordLink(organizationId, membershipId) {
    return unwrap(
      http.post(
        `/platform/organizations/${encodeURIComponent(organizationId)}/users/${encodeURIComponent(membershipId)}/password-link`,
      ),
      isObject,
    )
  },

  /* ─── Prompts ───────────────────────────────────────────────────────────── */
  listPromptRules() {
    return unwrap(http.get('/platform/prompt-rules'))
  },

  createPromptRule(payload) {
    return unwrap(http.post('/platform/prompt-rules', payload), isObject)
  },

  listPromptTemplates() {
    return unwrap(http.get('/platform/prompt-templates'))
  },

  createPromptTemplate(payload) {
    return unwrap(http.post('/platform/prompt-templates', payload), isObject)
  },

  templateVersions(id) {
    return unwrap(http.get(`/platform/prompt-templates/${encodeURIComponent(id)}/versions`))
  },

  createTemplateVersion(id, payload) {
    return unwrap(
      http.post(`/platform/prompt-templates/${encodeURIComponent(id)}/versions`, payload),
      isObject,
    )
  },

  /** Prueba de un cambio contra conversaciones reales anonimizadas. */
  simulateTemplate(id, payload, config = {}) {
    return unwrap(
      http.post(`/platform/prompt-templates/${encodeURIComponent(id)}/simulate`, payload, config),
      isObject,
    )
  },

  /** Despliegue: a un cliente, a un grupo o a todos. */
  publishTemplate(id, payload) {
    return unwrap(
      http.post(`/platform/prompt-templates/${encodeURIComponent(id)}/publish`, payload),
      isObject,
    )
  },

  /* ─── Proveedores y modelos ─────────────────────────────────────────────── */
  //
  // La API devuelve aquí las filas crudas de PostgreSQL (`base_url`,
  // `api_key_env`, `display_name`, `p95_latency_ms`…). Se traducen en esta capa
  // y no en la plantilla porque el desajuste ya costó una pantalla entera de
  // guiones sobre datos que existían: la vista pide nombres del panel y esta
  // función se encarga de encontrarlos venga como venga la respuesta.

  async listProviders(config = {}) {
    const data = await unwrap(http.get('/platform/providers', config))
    return asList(data).map(normalizeProvider)
  },

  /*
   * Aquí vivían `createProvider` y `createModel`.
   *
   * El alta de modelo la retiró el backend con un 501 explicativo: escribía en
   * la tabla `models`, que el motor no consulta. El alta de PROVEEDOR es el
   * mismo caso y seguía en pie: la fila que insertaba sólo la lee la pantalla
   * que la creó, porque el router resuelve sus proveedores desde
   * `config/models.yml`. Un operador daba de alta «Groq», veía su tarjeta
   * aparecer con su URL y su variable de entorno, y no había ganado nada: el
   * motor seguía sin saber hablar con Groq.
   *
   * Dar de alta un proveedor es editar `config/models.yml` y desplegar. Se
   * retira el cliente entero y no sólo el botón para que nadie vuelva a
   * cablearlo sin hacerse antes esa pregunta.
   */

  /**
   * Catálogo COMPLETO, tal y como lo declara `config/models.yml`.
   *
   * Devuelve el sobre con `catalog: { source, available, reviewedAt }`, que el
   * backend manda A PROPÓSITO y esta capa descartaba. Sin él, una lista vacía
   * es ambigua: puede ser que no haya modelos o que no se haya podido leer el
   * archivo del catálogo tras un despliegue. La pantalla decía «Catálogo vacío:
   * registra un proveedor» en los dos casos, y ese consejo ya ni siquiera es
   * posible.
   */
  async listModels(config = {}) {
    const data = await unwrap(http.get('/platform/models', config))
    return { items: asList(data).map(normalizeModel), catalog: normalizeCatalogMeta(data) }
  },

  /**
   * Los modelos que de verdad se pueden usar hoy: los de proveedores con clave.
   *
   * El catálogo trae 27 modelos de cinco proveedores porque es la lista de con
   * quién SABE hablar Iris. Enseñarlos todos, con sus precios y sus tamaños de
   * contexto, invitaba a elegir cualquiera de ellos cuando sólo hay clave de
   * uno: un catálogo de precios de cosas que no se pueden comprar.
   *
   * Se cruza contra `/platform/providers`, que es el único extremo que dice si
   * hay clave cargada (`configured`). Las dos peticiones van en paralelo porque
   * no dependen entre sí, y comparten el `signal` de cancelación de la vista.
   */
  async listUsableModels(config = {}) {
    const [catalog, providers] = await Promise.all([
      this.listModels(config),
      this.listProviders(config),
    ])
    return selectUsableModels(catalog.items, providers)
  },

  /**
   * Latencia y errores medidos con tráfico real, no con un ping.
   *
   * `notes.errorRateIsLowerBound` viaja hasta la vista sin tocarlo. El backend
   * lo manda porque hoy sólo se anotan las llamadas que SALEN BIEN: la tasa de
   * error es una cota inferior, no una medición. Descartarlo aquí hacía que la
   * pantalla pintara «0 %» con el mismo aspecto que un cero medido, que es
   * justo lo contrario de lo que el backend estaba intentando avisar.
   */
  async modelsHealth(config = {}) {
    const data = await unwrap(http.get('/platform/models/health', config))
    return {
      items: asList(data).map(normalizeModelHealth),
      since: data?.since ?? null,
      errorRateIsLowerBound: data?.notes?.errorRateIsLowerBound === true,
    }
  },

  /* ─── Planes y consumo ──────────────────────────────────────────────────── */
  async listPlans(config = {}) {
    const data = await unwrap(http.get('/platform/plans', config))
    return asList(data).map(normalizePlan)
  },

  createPlan(payload) {
    return unwrap(http.post('/platform/plans', payload), isObject)
  },

  updatePlan(id, patch) {
    return unwrap(http.patch(`/platform/plans/${encodeURIComponent(id)}`, patch), isObject)
  },

  /**
   * Consumo y márgenes del periodo.
   *
   * OJO con el filtro temporal: este extremo NO acepta `from`/`to`, sólo
   * `period` con formato `YYYY-MM`. Y como la validación del backend usa
   * `stripUnknown`, mandar un rango no daba error: se descartaba en silencio y
   * respondía el mes en curso. La vista ofrecía «últimos 7 / 30 / 90 días» y
   * enseñaba siempre lo mismo sin que nada lo delatara.
   *
   * @param {{ groupBy?: 'organization'|'model'|'task'|'channel', period?: string }} params
   */
  async usage(params = {}, config = {}) {
    const { groupBy = 'organization', period = toPeriod() } = params
    const data = await unwrap(
      http.get('/platform/usage', { ...config, params: toQuery({ groupBy, period }) }),
      isObject,
    )
    return {
      groupBy: data?.groupBy ?? groupBy,
      period: data?.period ?? period,
      items: asList(data).map(normalizePlatformUsageRow),
    }
  },

  /* ─── Operación ─────────────────────────────────────────────────────────── */
  async listOpenwaNodes(config = {}) {
    const data = await unwrap(http.get('/platform/openwa-nodes', config))
    return asList(data).map(normalizeOpenwaNode)
  },

  createOpenwaNode(payload) {
    return unwrap(http.post('/platform/openwa-nodes', payload), isObject)
  },

  /**
   * Estado de las colas de trabajo.
   *
   * El sobre de este extremo es el único de toda la consola que NO usa `items`:
   * responde `{ queues, measured, notMeasured, reason, updatedAt }`. Y `queues`
   * puede ser `null`: es lo que llega cuando Redis no responde, y es distinto de
   * una lista vacía. `[]` diría «he mirado y no hay nada encolado»; `null` dice
   * «no he podido mirar», que suele ser justo lo contrario.
   */
  async queues(config = {}) {
    const data = await unwrap(http.get('/platform/operations/queues', config), isObject)
    return normalizeQueuesStatus(data)
  },

  /**
   * Entregas de webhook fallidas. Hoy `items: null` con `measured: false`: no
   * existe tabla de intentos ni de fallos de entrega, así que esta lista no
   * puede estar ni vacía ni llena.
   */
  async webhookFailures(params = {}, config = {}) {
    const data = await unwrap(
      http.get('/platform/operations/webhook-failures', { ...config, params: toQuery(params) }),
    )
    return normalizeWebhookFailures(data)
  },

  /**
   * Registro global de auditoría: quién hizo qué, cuándo y sobre qué.
   *
   * Filas crudas en snake_case. La vista buscaba `createdAt`, `actor.email`,
   * `organization.name` y `entity`; ninguno de esos cuatro caminos existe, así
   * que cada entrada salía sin fecha y sin autor — es decir, sin lo único que
   * hace útil a un registro de auditoría.
   */
  async auditLog(params = {}, config = {}) {
    const data = await unwrap(
      http.get('/platform/audit-log', { ...config, params: toQuery(params) }),
    )
    return {
      items: asList(data).map(normalizeAuditEntry),
      nextCursor: Array.isArray(data) ? null : (data?.nextCursor ?? null),
    }
  },

  /* ─── Correos del sistema ───────────────────────────────────────────────── */
  //
  // Iris manda invitaciones y avisos por correo. Cuando uno no llega, el
  // operador necesita ver el estado y el motivo del fallo; lo que NO necesita
  // —ni debe poder— es ver el enlace de invitación, que es una credencial
  // portadora. Por eso la respuesta se pasa por la lista blanca del contrato
  // AQUÍ, en la frontera: si un día el backend empieza a devolverlo, el enlace
  // muere en esta función y no llega a ninguna vista.

  /**
   * Bandeja de salida, paginada por cursor.
   * @param {{ status?: string, limit?: number, cursor?: string }} [params]
   * @returns {Promise<{ items: object[], nextCursor: string|null }>}
   */
  async listNotifications(params = {}, config = {}) {
    const data = await unwrap(
      http.get('/platform/notifications', { ...config, params: toQuery(params) }),
    )
    // El sobre se acepta en las dos formas que ya conviven en el panel (página
    // del contrato o lista pelada): una pantalla de diagnóstico que se cae
    // entera porque falta `nextCursor` es peor que una que degrada.
    const items = Array.isArray(data) ? data : (data?.items ?? [])
    warnAboutUnexpectedFields(items)

    return {
      items: items.map(normalizeNotification),
      nextCursor: Array.isArray(data) ? null : (data?.nextCursor ?? null),
    }
  },

  /**
   * Reencola el envío. El backend responde 202: lo devuelto es el elemento
   * puesto en cola, **no** un correo ya entregado.
   * @param {string} id
   */
  async resendNotification(id) {
    const data = await unwrap(
      http.post(`/platform/notifications/${encodeURIComponent(id)}/resend`),
      isObject,
    )
    warnAboutUnexpectedFields([data])
    return normalizeNotification(data)
  },
}

/**
 * Avisa por consola —una vez por respuesta y sólo con los NOMBRES de los
 * campos— cuando la API devuelve algo que el contrato no contempla. Los valores
 * no se registran nunca: el campo que más probablemente se cuele es el enlace de
 * invitación, y volcarlo en la consola sería filtrarlo por la otra puerta.
 *
 * @param {object[]} items
 */
/**
 * Las listas del catálogo llegan unas veces como `{ items }` y otras como array
 * pelado, según el extremo. Se resuelve una vez aquí.
 */
function asList(data) {
  if (Array.isArray(data)) return data
  return Array.isArray(data?.items) ? data.items : []
}

/**
 * Procedencia del catálogo de modelos.
 *
 * `available: false` significa «no pude leer `config/models.yml`», y es una
 * situación distinta de «el archivo está y no declara modelos». Un backend
 * anterior a este campo no manda nada: entonces `available` queda en `null` y la
 * vista no afirma ni una cosa ni la otra.
 */
function normalizeCatalogMeta(data) {
  const meta = data?.catalog
  if (!meta || typeof meta !== 'object') {
    return { source: null, available: null, reviewedAt: null }
  }
  return {
    source: typeof meta.source === 'string' ? meta.source : null,
    available: typeof meta.available === 'boolean' ? meta.available : null,
    reviewedAt: typeof meta.reviewedAt === 'string' ? meta.reviewedAt : null,
  }
}

function warnAboutUnexpectedFields(items) {
  const extra = new Set()
  for (const item of items) {
    for (const field of unexpectedNotificationFields(item)) extra.add(field)
  }
  if (extra.size === 0) return
  console.warn(
    `[platform/notifications] la API devolvió campos fuera del contrato y se han descartado: ${[...extra].join(', ')}`,
  )
}
